import axios, { AxiosInstance } from 'axios'
import qs from 'qs'
import get from 'lodash/get'
import pickBy from 'lodash/pickBy'
import includes from 'lodash/includes'
import { HttpService } from '@lk/api'

import { getToken } from '../store/auth/auth.selectors'

//@ts-ignore
import { actions as LKModalActions } from 'shared/LKModal'

enum ErrorCodes {
  Unauthorized = 99,
}

const ALLOWED_VALUE_TYPES = ['string', 'number']

const getErrorMessages = (errorCode: number) => {
  switch (errorCode) {
    case 403:
      return 'Forbidden'
    case 404:
      return 'Not Found'
    case 500:
      return 'Internal Server Error'
    case 502:
      return 'Bad Gateway'
  }
}

export const makeHttpService = (): HttpService => {
  // allows values by type (For example it will allow 0 and '' to be stringified)
  // otherwise it will ignore in case of falsy value
  const pickByIdentity = (value) => includes(ALLOWED_VALUE_TYPES, typeof value) || value

  return axios.create({
    transformRequest: [
      (data, _headers) => {
        if (data) {
          const isFormData = Object.prototype.toString.call(data) === '[object FormData]'
          if (isFormData) {
            return data
          }
        }

        return qs.stringify(pickBy(data, pickByIdentity))
      },
    ],
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
}

export function applySessionExpirationInterceptor(http: HttpService & AxiosInstance, store: any, config: any): void {
  http.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      if (error.Code === ErrorCodes.Unauthorized) {
        // unauthorize
      }

      return Promise.reject(error)
    },
  )
}

export function applyGeneralInterceptor(http: HttpService & AxiosInstance, store: any, config: any): void {
  http.defaults.transformResponse = [
    function (data, _headers, status) {
      if (status === 403 || status === 404 || status === 500 || status === 502) {
        throw new Error('error code', {
          cause: new Error(`<span style="color: red">${status}</span> ${getErrorMessages(status)}`),
        })
      }

      // Attempt to parse the data as JSON
      try {
        if (data instanceof Blob) return data
        return JSON.parse(data)
      } catch (error) {
        throw new Error('parsing error', { cause: new Error(data) })
      }
    },
  ]
  http.interceptors.response.use(
    async (response) => {
      if (get(response, 'headers.content-type').includes('json')) {
        let blobData
        let containsErrors
        try {
          blobData = await new Response(response.data).text()
          containsErrors = blobData.indexOf('"Error"') >= 0
        } catch (error) {
          // console.log(error)
        }

        if (containsErrors) {
          const decodedResponse = JSON.parse(blobData)
          if (decodedResponse.Error.Code !== 0) {
            const objectToReturn = {
              ...decodedResponse.Error,
              data: decodedResponse.Data,
            }
            return Promise.reject(objectToReturn)
          }
        } else if (get(response, 'data.Error.Code') !== 0) {
          return Promise.reject({
            ...response.data.Error,
            data: get(response, 'data.Data'),
          })
        }

        if (response.data.Apps) {
          response.data = response.data.Apps
        } else if (response.data.ClientDupCheckResponse) {
          response.data = {
            ClientDupCheckResponse: response.data.ClientDupCheckResponse,
            ...response.data.Data,
          }
        } else if (response.data.ReqFields) {
          response.data = {
            ReqFields: response.data.ReqFields,
            ...response.data.Data,
          }
        } else if (response.data.GridState) {
          response.data = response.data.GridState
        } else {
          response.data = response.data.Data
        }

        // response.data = response.data.Apps ? response.data.Apps : response.data.Data
      } else {
        const url = URL.createObjectURL(response.data)
        const contentType = get(response, 'headers.content-type')

        response.data = {
          url,
          contentType,
        }
      }

      return response
    },
    (error) => {
      if (error.message === 'error code' || error.message === 'parsing error') {
        store.dispatch(
          LKModalActions.openError(`<ul><div style="text-align: center;">${error.cause.message}</div></ul>`),
        )
      }
      return Promise.reject(error)
    },
  )
}

export function applyTokenInterceptor(http: HttpService & AxiosInstance, store: any, config: any): void {
  http.interceptors.request.use((config) => {
    const token = getToken(store.getState())
    if (token) {
      config.headers.STK = token
    }

    //@ts-ignore
    config.headers = {
      ...config.headers,
    }

    return config
  })
}

export const applyResponseInterceptors = (http: HttpService & AxiosInstance, store: any, config: any): void => {
  applyGeneralInterceptor(http, store, config)
  applySessionExpirationInterceptor(http, store, config)
}

export const applyRequestInterceptors = (http: HttpService & AxiosInstance, store: any, config: any): void => {
  applyTokenInterceptor(http, store, config)
}
